import React, { useRef, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import Common from "../../helpers/common";
import { useNavigate } from "react-router-dom";
import {
  AuthenticationViewTypeEnum,
  respEnum,
  RoleEnum,
  SignUpTypeEnum,
  VerificationTypeEnum,
} from "../../constants/appConstants";
import { PhoneInputField } from "../formik-components";
import { useDispatch } from "react-redux";
import { setSignupData } from "../../store/signupSlice";
import Swal from 'sweetalert2';

const signupSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone: Yup.string().required("Phone number is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

const SignupModal = ({ open, onClose, onLoginClick, setViewType, onLoginSuccess }) => {
  const navigate = useNavigate();
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  if (!open) return null;

  const _handleSignup = async (values) => {
    if (values.password !== values.confirmPassword) {
      await Swal.fire({
        icon: 'error',
        title: 'Password Mismatch',
        text: 'Password and Confirm password do not match',
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#007bff'
      });
      formikRef.current.setFieldError(
        "confirmPassword",
        "Password and Confirm password do not match"
      );
      return;
    }

    try {
      setIsLoading(true);
      const signupData = {
        ...values,
        phone: values.phoneNum,
        signupType: SignUpTypeEnum.EmailorPhone,
      };

      const isEmailExist = await checkIsExist(signupData.email);
      if (isEmailExist == null) {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to check email existence. Please try again.',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#007bff'
        });
        setIsLoading(false);
        return;
      }
      if (isEmailExist) {
        await Swal.fire({
          icon: 'warning',
          title: 'Email Exists',
          text: 'This email is already registered. Please use a different email.',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#007bff'
        });
        formikRef.current.setFieldError("email", "Email already exists!");
        setIsLoading(false);
        return;
      }

      const isPhoneExist = await checkIsExist(signupData.phone);
      if (isPhoneExist == null) {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to check phone existence. Please try again.',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#007bff'
        });
        setIsLoading(false);
        return;
      }
      if (isPhoneExist) {
        await Swal.fire({
          icon: 'warning',
          title: 'Phone Exists',
          text: 'This phone number is already registered. Please use a different number.',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#007bff'
        });
        formikRef.current.setFieldError(
          "phone",
          "Phone number already exists!"
        );
        setIsLoading(false);
        return;
      }

      sendOtp(signupData);
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred during signup. Please try again.',
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#007bff'
      });
      setIsLoading(false);
    }
  };

  const sendOtp = async (signupData) => {
    setIsLoading(true);
    try {
      const response = await Common.ApiService.getInstance()?.request(
        "SendVerificationCode",
        {
          phoneCode: signupData["phoneCode"],
          phone: signupData["phone"],
          deviceId: "string",
          type: VerificationTypeEnum.accountVerificationPhone,
        },
        "POST"
      );

      if (response?.status == respEnum.Success) {
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response?.message || 'OTP sent successfully',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#007bff'
        });
        dispatch(setSignupData(signupData));
        setViewType(AuthenticationViewTypeEnum.Otp);
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response?.message || 'Failed to send OTP',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#007bff'
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to send OTP. Please try again.',
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#007bff'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkIsExist = async (value) => {
    try {
      const response = await Common.ApiService.getInstance()?.request(
        `IsUserExist?emailorphone=${value}`,
        null,
        "POST"
      );
      return response.data;
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to check user existence. Please try again.',
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#007bff'
      });
      return null;
    }
  };

  return (
    <div className="popup">
      <div
        className="popup-inner"
        style={{
          backgroundColor: "#1a1a1a",
          border: "1px solid",
          color: "white",
          top: "55%",
          maxWidth: "400px",
          width: "90%",
          boxShadow: "0 0 20px rgba(255, 255, 255, 0.1)",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            right: "15px",
            top: "15px",
            background: "none",
            border: "none",
            color: "white",
            fontSize: "28px",
            cursor: "pointer",
            padding: "5px 10px",
            lineHeight: "1",
            zIndex: "1000",
          }}
        >
          ×
        </button>

        <div
          style={{
            textAlign: "center",
            marginBottom: "0px",
            marginTop: "10px",
          }}
        >
          <img
            src="/images/logo_white.svg"
            alt="Ignite Sports"
            style={{
              width: "120px",
              height: "auto",
            }}
          />
        </div>

        <h2
          style={{
            textAlign: "center",
            color: "white",
            marginBottom: "0px",
            fontSize: "28px",
            fontWeight: "bold",
          }}
        >
          Create Account
        </h2>

        <Formik
          innerRef={formikRef}
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            password: "",
            confirmPassword: "",
          }}
          validationSchema={signupSchema}
          onSubmit={(values) => {
            console.log("Signup submitted:", values);
            _handleSignup(values);
          }}
        >
          {(formikProps) => (
            <div>
              <div style={{ marginBottom: "0px" }}>
                <label
                  style={{
                    color: "#999",
                    marginBottom: "0px",
                    display: "block",
                    fontSize: "14px",
                  }}
                >
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formikProps.values.firstName}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  placeholder="First Name"
                  style={{
                    width: "100%",
                    padding: "4px",
                    backgroundColor: "#2d2d2d",
                    border: "1px solid #3d3d3d",
                    borderRadius: "4px",
                    color: "white",
                    fontSize: "16px",
                  }}
                />
                {formikProps.touched.firstName &&
                  formikProps.errors.firstName && (
                    <div
                      style={{
                        color: "#ff4d4d",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {formikProps.errors.firstName}
                    </div>
                  )}
              </div>
              <div style={{ marginBottom: "0px" }}>
                <label
                  style={{
                    color: "#999",
                    marginBottom: "0px",
                    display: "block",
                    fontSize: "14px",
                  }}
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formikProps.values.lastName}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  placeholder="Last Name"
                  style={{
                    width: "100%",
                    padding: "4px",
                    backgroundColor: "#2d2d2d",
                    border: "1px solid #3d3d3d",
                    borderRadius: "4px",
                    color: "white",
                    fontSize: "16px",
                  }}
                />
                {formikProps.touched.lastName &&
                  formikProps.errors.lastName && (
                    <div
                      style={{
                        color: "#ff4d4d",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {formikProps.errors.lastName}
                    </div>
                  )}
              </div>

              <div style={{ marginBottom: "0px" }}>
                <label
                  style={{
                    color: "#999",
                    marginBottom: "0px",
                    display: "block",
                    fontSize: "14px",
                  }}
                >
                  EMAIL *
                </label>
                <input
                  type="email"
                  name="email"
                  {...formikProps.getFieldProps("email")}
                  style={{
                    width: "100%",
                    padding: "4px",
                    backgroundColor: "#2d2d2d",
                    border: "1px solid #3d3d3d",
                    borderRadius: "4px",
                    color: "white",
                    fontSize: "16px",
                  }}
                />
                {formikProps.touched.email && formikProps.errors.email && (
                  <div
                    style={{
                      color: "#ff4d4d",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    {formikProps.errors.email}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "0px" }}>
                <PhoneInputField formikProps={formikProps} />
              </div>

              <div style={{ marginBottom: "0px" }}>
                <label
                  style={{
                    marginTop: "5px",
                    color: "#999",
                    marginBottom: "0px",
                    display: "block",
                    fontSize: "14px",
                  }}
                >
                  PASSWORD *
                </label>
                <input
                  type="password"
                  name="password"
                  {...formikProps.getFieldProps("password")}
                  style={{
                    width: "100%",
                    padding: "4px",
                    backgroundColor: "#2d2d2d",
                    border: "1px solid #3d3d3d",
                    borderRadius: "4px",
                    color: "white",
                    fontSize: "16px",
                  }}
                />
                {formikProps.touched.password &&
                  formikProps.errors.password && (
                    <div
                      style={{
                        color: "#ff4d4d",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {formikProps.errors.password}
                    </div>
                  )}
              </div>

              <div style={{ marginBottom: "0px" }}>
                <label
                  style={{
                    color: "#999",
                    marginBottom: "0px",
                    display: "block",
                    fontSize: "14px",
                  }}
                >
                  CONFIRM PASSWORD *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  {...formikProps.getFieldProps("confirmPassword")}
                  style={{
                    width: "100%",
                    padding: "4px",
                    backgroundColor: "#2d2d2d",
                    border: "1px solid #3d3d3d",
                    borderRadius: "4px",
                    color: "white",
                    fontSize: "16px",
                  }}
                />
                {formikProps.touched.confirmPassword &&
                  formikProps.errors.confirmPassword && (
                    <div
                      style={{
                        color: "#ff4d4d",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {formikProps.errors.confirmPassword}
                    </div>
                  )}
              </div>

              <button
                type="submit"
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  color: "black",
                  marginTop: "0px",
                  padding: "4px",
                  borderRadius: "4px",
                  border: "none",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                }}
                disabled={isLoading}
                onClick={formikProps.handleSubmit}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#e6e6e6")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
              >
                {isLoading ? "Verifying..." : "Verify information"}
              </button>

              <div
                style={{ textAlign: "center", marginTop: "0px", color: "#999" }}
              >
                Have an account?{" "}
                <button
                  type="button"
                  onClick={onLoginClick}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "14px",
                  }}
                >
                  Log In
                </button>
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default SignupModal;
